import { Helmet } from "react-helmet-async";
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
// @mui
import {
  Card,
  Stack,
  Button,
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from "@mui/material";

// components
import { Edit } from "@mui/icons-material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import Protected from "../protected/Protected";
import Label from "../components/label/Label";
// sections
import CustomTable from "../components/data-table/CustomDataTable";
import { useGetGoogleAnalyticsSectionsQuery } from "../redux/services/googleAnalyticsSection.service";
import {
  useGetGoogleAnalyticsConfigsQuery,
  useGetAllKeysQuery,
} from "../redux/services/googleAnalyticsConfig.service";
import GoogleAnalyticsSectionForm from "../sections/@dashboard/dashboard-management/googleAnalyticsSection.form";
import GoogleAnalyticsConfigForm from "../sections/@dashboard/dashboard-management/googleAnalyticsConfig.form";
import { selectAbility } from "../redux/slices/casl.slice";
import { useGetRolesQuery } from "../redux/services/role.service";
import { useGetAuthUsersQuery } from "../redux/services/authUser.service";
// ----------------------------------------------------------------------

export default function DashboardManagementPage() {
  const ability = useSelector(selectAbility);

  const [tabValue, setTabValue] = useState(0);

  // Section state
  const [openSectionForm, setOpenSectionForm] = useState(false);
  const [openSectionRow, setOpenSectionRow] = useState(null);
  const [editSection, setEditSection] = useState(false);

  // Config state
  const [openConfigForm, setOpenConfigForm] = useState(false);
  const [openConfigRow, setOpenConfigRow] = useState(null);
  const [editConfig, setEditConfig] = useState(false);

  // Query parameters for Config table
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedKeyName, setSelectedKeyName] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [selectedAuthUserId, setSelectedAuthUserId] = useState("");

  // Fetch sections for dropdown
  const { data: sectionsData, isLoading: isLoadingSections } =
    useGetGoogleAnalyticsSectionsQuery({});

  // Fetch keys based on selected section (passes graphId to API)
  const { data: keysData, isLoading: isLoadingKeys } = useGetAllKeysQuery({
    ...(selectedSectionId && { graphId: selectedSectionId }),
  });

  // Fetch roles and auth users for dropdowns
  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({});
  const { data: authUsersData, isLoading: isLoadingAuthUsers } =
    useGetAuthUsersQuery({ isAuthAllInfo: true });

  const rolesMap = useMemo(() => {
    const map = {};
    rolesData?.data?.forEach((role) => {
      map[role.id] = role.name;
    });
    return map;
  }, [rolesData]);

  const authUsersMap = useMemo(() => {
    const map = {};
    authUsersData?.data?.forEach((user) => {
      map[user.id] = user.email;
    });
    return map;
  }, [authUsersData]);

  const sectionColumns = useMemo(
    () => [
      {
        field: "graphName",
        headerName: "Graph Name",
        minWidth: 200,
        flex: 1,
        isSortable: true,
        isFilterable: true,
        defaultVisible: true,
      },
      {
        field: "roles",
        headerName: "Roles",
        minWidth: 200,
        flex: 1,
        isSortable: false,
        isFilterable: true,
        defaultVisible: true,
        renderCell: ({ row }) => {
          const rolesList = row?.roles || row?.roleIds || [];
          if (!rolesList || rolesList.length === 0) return "--";
          const names = rolesList
            .map((r) =>
              typeof r === "object" ? r?.name || r?.id : rolesMap[r] || r
            )
            .filter(Boolean);
          return names.length > 0 ? names.join(", ") : "--";
        },
      },
      {
        field: "authUsers",
        headerName: "Auth Users",
        minWidth: 220,
        flex: 1,
        isSortable: false,
        isFilterable: true,
        defaultVisible: true,
        renderCell: ({ row }) => {
          const usersList = row?.authUsers || row?.authUserIds || [];
          if (!usersList || usersList.length === 0) return "--";
          const emails = usersList
            .map((u) =>
              typeof u === "object" ? u?.email || u?.id : authUsersMap[u] || u
            )
            .filter(Boolean);
          return emails.length > 0 ? emails.join(", ") : "--";
        },
      },
    ],
    [rolesMap, authUsersMap]
  );

  const configColumns = useMemo(
    () => [
      {
        field: "keyName",
        headerName: "Key Name",
        minWidth: 200,
        flex: 1,
        isSortable: true,
        isFilterable: true,
        defaultVisible: true,
      },
      {
        field: "graphSection",
        headerName: "Graph Section",
        minWidth: 180,
        flex: 1,
        isSortable: true,
        isFilterable: true,
        defaultVisible: true,
        renderCell: ({ row }) =>
          row?.graphSection?.graphName || row?.graphName || "--",
      },
      {
        field: "isAccessible",
        headerName: "Accessible",
        minWidth: 130,
        isSortable: true,
        isFilterable: true,
        defaultVisible: true,
        type: "boolean",
        valueFormatter: (value) => (value ? "Yes" : "No"),
        renderCell: (params) => (
          <Label color={params.value ? "success" : "error"}>
            {params.value ? "Yes" : "No"}
          </Label>
        ),
      },
      {
        field: "roles",
        headerName: "Roles",
        minWidth: 200,
        flex: 1,
        isSortable: false,
        isFilterable: true,
        defaultVisible: true,
        renderCell: ({ row }) => {
          const rolesList = row?.roles || row?.roleIds || [];
          if (!rolesList || rolesList.length === 0) return "--";
          const names = rolesList
            .map((r) =>
              typeof r === "object" ? r?.name || r?.id : rolesMap[r] || r
            )
            .filter(Boolean);
          return names.length > 0 ? names.join(", ") : "--";
        },
      },
      {
        field: "authUsers",
        headerName: "Auth Users",
        minWidth: 220,
        flex: 1,
        isSortable: false,
        isFilterable: true,
        defaultVisible: true,
        renderCell: ({ row }) => {
          const usersList = row?.authUsers || row?.authUserIds || [];
          if (!usersList || usersList.length === 0) return "--";
          const emails = usersList
            .map((u) =>
              typeof u === "object" ? u?.email || u?.id : authUsersMap[u] || u
            )
            .filter(Boolean);
          return emails.length > 0 ? emails.join(", ") : "--";
        },
      },
    ],
    [rolesMap, authUsersMap]
  );

  // Reset keyName when section changes
  const handleSectionChange = (e) => {
    setSelectedSectionId(e.target.value);
    setSelectedKeyName(""); // Reset key name when section changes
  };

  const handleResetConfigFilter = () => {
    setSelectedSectionId("");
    setSelectedKeyName("");
    setSelectedRoleId("");
    setSelectedAuthUserId("");
  };

  const configQueryParams = useMemo(() => {
    const params = {};
    if (selectedSectionId) params.graphId = selectedSectionId;
    if (selectedKeyName) params.keyName = selectedKeyName;
    if (selectedRoleId) params.roleId = selectedRoleId;
    if (selectedAuthUserId) params.authUserId = selectedAuthUserId;
    return params;
  }, [selectedSectionId, selectedKeyName, selectedRoleId, selectedAuthUserId]);

  const handleCloseSectionForm = () => {
    setOpenSectionForm(false);
    setEditSection(false);
  };

  const handleCloseConfigForm = () => {
    setOpenConfigForm(false);
    setEditConfig(false);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Protected>
      <Helmet>
        <title> Dashboard Management </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4" gutterBottom>
            Dashboard Management
          </Typography>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Google Analytics Sections" />
            <Tab label="Google Analytics Configs" />
          </Tabs>
        </Box>

        {/* Google Analytics Sections Table */}
        {tabValue === 0 && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography variant="h6">Graph Sections</Typography>
              {ability.can("Is", "admin") && (
                <Button
                  variant="contained"
                  onClick={() => setOpenSectionForm(true)}
                  startIcon={<Iconify icon="eva:plus-fill" />}
                >
                  New Section
                </Button>
              )}
            </Stack>

            <Card>
              <Scrollbar>
                <CustomTable
                  canSelect
                  useDataQuery={useGetGoogleAnalyticsSectionsQuery}
                  tableName="GoogleAnalyticsSection"
                  searchLabel="Search Section..."
                  searchCols={["graphName"]}
                  columnsOverride={sectionColumns}
                  actions={
                    ability.can("Is", "admin")
                      ? {
                          field: "actions",
                          headerName: "Action",
                          type: "actions",
                          getActions: ({ row }) => [
                            <GridActionsCellItem
                              key="edit"
                              icon={<Edit />}
                              label="Edit"
                              showInMenu
                              onClick={() => {
                                setOpenSectionRow(row);
                                setEditSection(true);
                                setOpenSectionForm(true);
                              }}
                            />,
                          ],
                        }
                      : null
                  }
                />
              </Scrollbar>
            </Card>

            {ability.can("Is", "admin") && (
              <GoogleAnalyticsSectionForm
                open={openSectionForm}
                onClose={handleCloseSectionForm}
                edit={editSection}
                openRow={openSectionRow}
              />
            )}
          </>
        )}

        {/* Google Analytics Configs Table */}
        {tabValue === 1 && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Typography variant="h6">Graph Field Configs</Typography>
              {ability.can("Is", "admin") && (
                <Button
                  variant="contained"
                  onClick={() => setOpenConfigForm(true)}
                  startIcon={<Iconify icon="eva:plus-fill" />}
                >
                  New Config
                </Button>
              )}
            </Stack>

            {/* Section Name, Key Name, Role, and Auth User Dropdowns */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="section-filter-label">Section Name</InputLabel>
                  <Select
                    labelId="section-filter-label"
                    value={selectedSectionId}
                    label="Section Name"
                    onChange={handleSectionChange}
                  >
                    <MenuItem value="">
                      <em>All Sections</em>
                    </MenuItem>
                    {isLoadingSections ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      sectionsData?.data?.map((section) => (
                        <MenuItem key={section.id} value={section.id}>
                          {section.graphName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="keyName-filter-label">Key Name</InputLabel>
                  <Select
                    labelId="keyName-filter-label"
                    value={selectedKeyName}
                    label="Key Name"
                    onChange={(e) => setSelectedKeyName(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All Keys</em>
                    </MenuItem>
                    {isLoadingKeys ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      keysData?.data?.map((key) => (
                        <MenuItem key={key} value={key}>
                          {key}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="role-filter-label">Role</InputLabel>
                  <Select
                    labelId="role-filter-label"
                    value={selectedRoleId}
                    label="Role"
                    onChange={(e) => setSelectedRoleId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All Roles</em>
                    </MenuItem>
                    {isLoadingRoles ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      rolesData?.data?.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="authUser-filter-label">Auth User</InputLabel>
                  <Select
                    labelId="authUser-filter-label"
                    value={selectedAuthUserId}
                    label="Auth User"
                    onChange={(e) => setSelectedAuthUserId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All Users</em>
                    </MenuItem>
                    {isLoadingAuthUsers ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      authUsersData?.data?.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          {user.email}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Card>
              <Scrollbar>
                <CustomTable
                  canSelect
                  useDataQuery={useGetGoogleAnalyticsConfigsQuery}
                  tableName="GoogleAnalyticsConfig"
                  searchLabel="Search Config..."
                  searchCols={["keyName"]}
                  columnsOverride={configColumns}
                  defaultQueryParams={configQueryParams}
                  resetParentForm={handleResetConfigFilter}
                  actions={
                    ability.can("Is", "admin")
                      ? {
                          field: "actions",
                          headerName: "Action",
                          type: "actions",
                          getActions: ({ row }) => [
                            <GridActionsCellItem
                              key="edit"
                              icon={<Edit />}
                              label="Edit"
                              showInMenu
                              onClick={() => {
                                setOpenConfigRow(row);
                                setEditConfig(true);
                                setOpenConfigForm(true);
                              }}
                            />,
                          ],
                        }
                      : null
                  }
                />
              </Scrollbar>
            </Card>

            {ability.can("Is", "admin") && (
              <GoogleAnalyticsConfigForm
                open={openConfigForm}
                onClose={handleCloseConfigForm}
                edit={editConfig}
                openRow={openConfigRow}
              />
            )}
          </>
        )}
      </Container>
    </Protected>
  );
}

